import { createContext, ReactNode, useCallback, useState, useRef } from 'react';
import without from 'lodash/without';

import { IBulkActionsContextValue } from './bulkActionsContext.interfaces';

const defaultContextValue = {
  selected: [],
  unselected: [],
  isAllSelected: false,
  filter: {},
  selectMedia: () => {},
  setIsAllSelected: () => {},
  checkIsSelected: () => {},
  resetState: () => {},
  setFilter: () => {},
};

const BulkActionsContext =
  // @ts-ignore
  createContext<IBulkActionsContextValue>(defaultContextValue);

const BulkActionsProvider = ({ children }: { children: ReactNode }) => {
  const [selected, setSelected] = useState<
    IBulkActionsContextValue['selected']
  >(defaultContextValue.selected);
  const [unselected, setUnselected] = useState<
    IBulkActionsContextValue['unselected']
  >(defaultContextValue.selected);
  const mediaMapRef = useRef({});

  const [isAllSelected, setIsAllSelected] = useState<IBulkActionsContextValue['isAllSelected']>(
    defaultContextValue.isAllSelected
  );
  const [filter, setFilter] = useState<IBulkActionsContextValue['filter']>({});
  const [allSelectedTotal, setAllSelectedTotal] =
    useState<IBulkActionsContextValue['allSelectedTotal']>(0);

  const selectMedia = useCallback(
    (media: { id: string; value: boolean }[]): void => {
      media.forEach(({ id, value }) => {
        if (value) {
          setSelected((prev) => Array.from(new Set([...prev, id])));
          setUnselected((prev) => Array.from(new Set(without(prev, id))));
        } else {
          setUnselected((prev) => Array.from(new Set([...prev, id])));
          setSelected((prev) => Array.from(new Set(without(prev, id))));
        }
      });
    },
    [setSelected, setUnselected]
  );

  const setIsAllSelectedSpy = useCallback(
    (value: boolean): void => {
      if (!value) {
        setUnselected([]);
        setSelected([]);
      }
      setIsAllSelected(value);
    },
    [setIsAllSelected, setSelected, setUnselected]
  );

  const resetState = useCallback((): void => {
    setUnselected([]);
    setSelected([]);
    setIsAllSelected(false);
    setFilter({});
    setAllSelectedTotal(0);
  }, [setAllSelectedTotal, setSelected, setIsAllSelected, setFilter]);

  const checkIsSelected = useCallback(
    (id: string) => {
      return isAllSelected ? !unselected.includes(id) : selected.includes(id);
    },
    [isAllSelected, selected, unselected]
  );

  return (
    <BulkActionsContext.Provider
      value={{
        selected,
        unselected,
        isAllSelected,
        selectMedia,
        checkIsSelected,
        setIsAllSelected,
        setIsAllSelectedSpy,
        resetState,
        filter,
        setFilter,
        allSelectedTotal,
        setAllSelectedTotal,
        mediaMapRef,
      }}
    >
      {children}
    </BulkActionsContext.Provider>
  );
};

export { BulkActionsContext, BulkActionsProvider };
